import React, { useRef } from 'react';
import Select, { DropdownIndicatorProps, components } from 'react-select';
import styled, { css } from 'styled-components';
import {
  SIZE_BORDER_RADIUS_DATA,
  SIZE_BORDER_RADIUS_ENUM,
  SIZE_FONT_DATA,
  SIZE_FONT_ENUM,
  SIZE_FONT_WEIGHT_DATA,
  SIZE_FONT_WEIGHT_ENUM,
} from '../../theme/size';
import { COLOR_ENUM } from '../../theme/color';
import { Spacing } from '../../theme';
import { PROPS_TYPE } from './constant';
import { TextElem } from '../text';
import { i18n } from '../../lib/lang';
import {
  VALUE_OPACITY_DATA,
  VALUE_OPACITY_ENUM,
  VALUE_TRANSITION_DATA,
  VALUE_TRANSITION_ENUM,
} from '../../theme/value';
import arrowIcon from '../../asset/svg/common/selectArrow.svg';
import checkIcon from '../../asset/svg/common/selectCheck.svg';
import { ReactComponent as IndicatorIcon } from '../../asset/svg/common/selectIndicator.svg';
import { ReactComponent as IndicatorIconWhite } from '../../asset/svg/common/selectIndicator-white.svg';
import { ReactComponent as CloseIcon } from '../../asset/svg/close-white.svg';

export const Elem: React.FC<PROPS_TYPE> = ({
  placeholder,
  onChange,
  defaultValue,
  title,
  options,
  name,
  value,
  error,
  errorMessage,
  noOptionsMessage,
  dynamic,
  onInputChange,

  textOnly = false,
  noAlertContainer = false,
  customComponents = {},
  className,
  isDisabled = undefined,
  isMulti = false,
  isRtl = false,
  closeMenuOnSelect = true,
  hideSelectedOptions = false,
  ref,
  formatGroupLabel,
  maxWidth = '100%',
  style,
  isClearable,
  inputValue,
  filterOption,
  menuIsOpen,
  isRequired,
}) => {
  const refSelect = useRef<any>();

  const handleChange = (e: any) => {
    if (onChange) onChange(name, e);
  };
  const noOptionsMessageFn = (inputValue: string) => {
    return !inputValue ? 'Почніть вводити' : 'Не знайдено';
  };

  const DropdownIndicator: React.FC<DropdownIndicatorProps> = (props) => {
    const isOpen = props.selectProps.menuIsOpen;
    return (
      <components.DropdownIndicator {...props}>
        {isOpen ? <IndicatorIconWhite /> : <IndicatorIcon />}
      </components.DropdownIndicator>
    );
  };

  const ClearIndicator: React.FC<DropdownIndicatorProps> = (props) => {
    return (
      <components.ClearIndicator {...props}>
        <CloseIcon />
      </components.ClearIndicator>
    );
  };

  return (
    <Container className={className} style={style}>
      {title && (
        <TitleContainer>
          <TextElem
            tid={title}
            size="semiSmall"
            type="regular"
            color="textSecondary"
          />
          {isRequired && (
            <TextElem tid="*" size="semiSmall" type="regular" color="error" />
          )}
        </TitleContainer>
      )}
      <CustomSelect
        value={value}
        classNamePrefix={'Select'}
        options={options}
        placeholder={placeholder && i18n.t(placeholder)}
        onChange={handleChange}
        name={name}
        isSearchable={dynamic || false}
        noOptionsMessage={({ inputValue }) =>
          noOptionsMessage ? noOptionsMessage : noOptionsMessageFn(inputValue)
        }
        filterOption={filterOption}
        onInputChange={onInputChange}
        menuIsOpen={menuIsOpen}
        menuPlacement="auto"
        textOnly={textOnly}
        defaultValue={defaultValue || 'select'}
        closeMenuOnSelect={isMulti ? false : closeMenuOnSelect}
        inputValue={inputValue}
        blurInputOnSelect={!isMulti}
        isDisabled={isDisabled}
        isMulti={isMulti}
        isRtl={isRtl}
        hideSelectedOptions={hideSelectedOptions}
        // controlShouldRenderValue={false}
        formatGroupLabel={formatGroupLabel}
        // autoFocus={true}
        ref={refSelect}
        isClearable={isClearable}
        components={{
          ...customComponents,
          DropdownIndicator,
          ClearIndicator,
        }}
        maxWidth={maxWidth}
      />

      {(error || errorMessage) && (
        <TextElem size="alert" color="error">
          {errorMessage}
        </TextElem>
      )}
    </Container>
  );
};

const TitleContainer = styled.div`
  display: inline-block;
`;

const Container = styled.div`
  display: grid;
  grid-gap: ${Spacing(3)};
  width: 100%;
`;

const CustomSelect = styled(Select)<{
  textOnly: boolean;
  isDisabled: true | undefined;
  maxWidth: string;
}>`
  *:has(+ &) {
    transition: all 0.2s;
  }

  &:hover {
    *:has(+ &) {
      /* color: ${({ theme }) => theme[COLOR_ENUM.TEXT_FOURTH]} !important; */
    }
  }

  .Select__option:active {
    background-color: transparent;
  }
  .Select__option--is-focused {
    background-color: transparent;
  }

  .Select__clear-indicator {
    color: ${({ theme }) => theme[COLOR_ENUM.TEXT_SECONDARY]} !important;
    bottom: 2px;
    width: 25px;
    height: 25px;
    position: relative;
  }

  .Select__multi-value {
    background-color: transparent;
    max-width: 25%;

    .Select__multi-value__label {
      color: ${({ theme }) => theme[COLOR_ENUM.TEXT_PRIMARY]} !important;
      font-weight: ${SIZE_FONT_WEIGHT_DATA[SIZE_FONT_WEIGHT_ENUM.MEDIUM]};
      font-size: ${SIZE_FONT_DATA[SIZE_FONT_ENUM.SEMI_SMALL]}px;
      padding: unset !important;
    }
  }

  .Select__multi-value:not(:nth-last-child(2)) ::after {
    background-color: transparent;

    content: ',';
  }

  .Select__multi-value__remove {
    display: none;
  }

  .Select__value-container--is-multi {
    text-overflow: ellipsis;
    display: flex !important;
    width: 10px !important;
    flex-wrap: nowrap !important;

    ${({ maxWidth }) => {
      if (maxWidth) {
        return css`
          max-width: calc(${maxWidth} - 40px);
        `;
      }
    }}

    font-size:14px !important;

    font-weight: 400;

    white-space: nowrap;
    overflow: hidden;
    display: initial;
  }

  /* #react-select-5-input {
    display: none;
  } */

  & .Select__control {
    border-radius: ${SIZE_BORDER_RADIUS_DATA[
      SIZE_BORDER_RADIUS_ENUM.DEFAULT
    ]}px;
    min-height: 44px;
    max-height: 44px;
    cursor: pointer;

    box-sizing: border-box;

    padding: ${Spacing(0)} ${Spacing(4)} ${Spacing(0)} ${Spacing(4)};
    width: 100%;

    box-shadow: none;

    border: 1px solid ${({ theme }) => theme[COLOR_ENUM.BORDER_DEFAULT]};
    background: ${({ theme }) => theme[COLOR_ENUM.BACKGROUND_SECONDARY]};

    &:hover {
      border: 1px solid ${({ theme }) => theme[COLOR_ENUM.BORDER_DEFAULT]};

      .Select__single-value {
        /* color: ${({ theme }) =>
          theme[COLOR_ENUM.SELECT_TEXT_HOVER]} !important; */
      }
    }
  }
  & .Select__control--is-focused {
    background: ${({ theme }) => theme[COLOR_ENUM.BACKGROUND_SECONDARY]};
  }
  & .Select__control--menu-is-open {
    *:has(+ &) {
      color: ${({ theme }) => theme[COLOR_ENUM.TEXT_SECONDARY]} !important;
    }
    &:hover {
      border: 1px solid ${({ theme }) => theme[COLOR_ENUM.BORDER_DEFAULT]};
    }
    & .Select__indicator.Select__dropdown-indicator {
      transform: rotate(180deg);
    }
    & .Select__placeholder {
      color: ${({ theme }) => theme[COLOR_ENUM.WHITE]};
    }

    border-radius: ${SIZE_BORDER_RADIUS_DATA[SIZE_BORDER_RADIUS_ENUM.DEFAULT]}px
      ${SIZE_BORDER_RADIUS_DATA[SIZE_BORDER_RADIUS_ENUM.DEFAULT]}px 0px 0px;
    border-bottom: 1px solid transparent;
  }
  & .Select__value-container {
    /* height: 1em; */
    padding: 0;
    height: 21px;
  }

  & .Select__input-container {
    margin: 0;
    padding: 0;
    font-weight: ${SIZE_FONT_WEIGHT_DATA[SIZE_FONT_WEIGHT_ENUM.REGULAR]};
    color: ${({ theme }) => theme[COLOR_ENUM.SELECT_TEXT_ACTIVE]} !important;
    font-size: ${SIZE_FONT_DATA[SIZE_FONT_ENUM.INPUT]}px;
    @media screen and (width<=600px) {
      font-size: ${SIZE_FONT_DATA[SIZE_FONT_ENUM.ALERT]}px;
    }
  }
  & .Select__single-value {
    color: ${({ theme }) => theme[COLOR_ENUM.WHITE]};

    font-size: ${SIZE_FONT_DATA[SIZE_FONT_ENUM.INPUT]}px;
  }

  .Select__control--menu-is-open {
    * > div {
      color: ${({ theme }) => theme[COLOR_ENUM.WHITE]} !important;
    }
  }
  & .Select__placeholder {
    margin: 0;
    color: ${({ theme }) => theme[COLOR_ENUM.TEXT_SECONDARY]};
    font-size: ${SIZE_FONT_DATA[SIZE_FONT_ENUM.INPUT]}px;
    @media screen and (width<=600px) {
      font-size: ${SIZE_FONT_DATA[SIZE_FONT_ENUM.ALERT]}px;
    }
    font-weight: ${SIZE_FONT_WEIGHT_DATA[SIZE_FONT_WEIGHT_ENUM.REGULAR]};

    &:hover {
      color: ${({ theme }) => theme[COLOR_ENUM.WHITE]};
    }
  }
  & .Select__indicator-separator {
    display: none;
  }
  & .Select__indicator.Select__dropdown-indicator {
    /* width: 17px; */
    color: #000;
    padding: 0;
    transition: all 0.2s;
    margin-left: ${Spacing(2)};
  }
  & .Select__menu {
    border-radius: 0px 0px
      ${SIZE_BORDER_RADIUS_DATA[SIZE_BORDER_RADIUS_ENUM.DEFAULT]}px
      ${SIZE_BORDER_RADIUS_DATA[SIZE_BORDER_RADIUS_ENUM.DEFAULT]}px;
    margin: 0;

    background-color: ${({ theme }) => theme[COLOR_ENUM.INPUT_ACTIVE]};
    border: 1px solid ${({ theme }) => theme[COLOR_ENUM.BORDER_DEFAULT]};
    box-shadow: none;
    z-index: 3;
    top: 44px;
  }

  & .Select__menu-list {
    padding: 0;

    border-radius: 0px 0px
      ${SIZE_BORDER_RADIUS_DATA[SIZE_BORDER_RADIUS_ENUM.DEFAULT]}px
      ${SIZE_BORDER_RADIUS_DATA[SIZE_BORDER_RADIUS_ENUM.DEFAULT]}px;
    background-color: ${({ theme }) => theme[COLOR_ENUM.INPUT_ACTIVE]};

    & :last-child {
      border-color: transparent;
    }
  }
  & .Select__menu-notice {
    text-align: left;
  }

  .Select__option.Select__option--is-focused {
    opacity: 0.7;
  }

  & .Select__option {
    transition: ${VALUE_TRANSITION_DATA[VALUE_TRANSITION_ENUM.EASE]};

    cursor: pointer;
    position: relative;
    color: ${({ theme }) => theme[COLOR_ENUM.TEXT_SECONDARY]};
    white-space: nowrap;
    display: block;
    margin: auto 0;
    overflow: hidden;
    text-align: start;
    text-overflow: ellipsis;
    padding: 15px ${Spacing(4)};

    border-bottom: 1px solid ${({ theme }) => theme[COLOR_ENUM.BORDER_DEFAULT]};
    font-size: ${SIZE_FONT_DATA[SIZE_FONT_ENUM.INPUT]}px;
    @media screen and (width<=600px) {
      font-size: ${SIZE_FONT_DATA[SIZE_FONT_ENUM.ALERT]}px;
    }
    height: 47px;
  }
  & .Select__option&:hover {
    /* background-color: ${({ theme }) =>
      theme[COLOR_ENUM.BACKGROUND_SECONDARY]}; */
  }
  & .Select__option--is-selected {
    color: ${({ theme }) => theme[COLOR_ENUM.WHITE]};
    background-color: transparent;
    position: relative;
  }

  & .Select__menu-list--is-multi > .Select__option::after {
    content: '';
    width: 16px !important;
    height: 16px !important;
    background: url(${checkIcon});
    display: block;
    position: absolute;
    right: 16px;
    top: 15px;
  }

  & .Select__menu-list--is-multi > .Select__option--is-selected::after {
    background: url(${arrowIcon});
  }

  & .Select__option--is-focused {
    background-color: transparent;
  }

  ${({ textOnly }) => {
    if (textOnly) {
      return css`
        & .Select__control {
          background-color: transparent;
          padding-top: 0;
          padding-bottom: 0;
          height: 50px;
        }
        & .Select__menu {
          border-radius: ${SIZE_BORDER_RADIUS_DATA[
            SIZE_BORDER_RADIUS_ENUM.DEFAULT
          ]}px;
          padding-top: 0;
          right: 0;
          width: 100%;
          min-width: fit-content;
          background: ${({ theme }) => theme[COLOR_ENUM.INPUT_ACTIVE]};
        }
        & .Select__menu-list {
          border-radius: ${SIZE_BORDER_RADIUS_DATA[
            SIZE_BORDER_RADIUS_ENUM.DEFAULT
          ]}px;
          background: ${({ theme }) => theme[COLOR_ENUM.INPUT_ACTIVE]};
        }
      `;
    }
  }}

  .Select__control--is-disabled {
    background-color: transparent;
    background: transparent;
  }

  ${({ isDisabled }) => {
    if (isDisabled) {
      return css`
        & .Select__control {
          opacity: ${VALUE_OPACITY_DATA[VALUE_OPACITY_ENUM.DEFAULT]};
        }
      `;
    }
  }}
`;
